import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, ChevronRight, FileText, GraduationCap, Package } from "lucide-react";
import { listCommerceOrders } from "lib/siteCommerceApi";
import { validOrder } from "pages/StoreOrderPage";
import { REFUND_RULE } from "components/ios/Alert";
import { useRemote } from "./Shared";
import { Tracker, buyerStatus, orderTitle, shortRef } from "./Orders";

const OPEN = (o) => !["expired", "cancelled"].includes(o.status) && !(o.status === "paid" && (o.kind === "course_enrollment" || o.fulfillmentStatus === "released"));
const when = (iso, timeZone) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : {
    wd: d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase(),
    num: d.getDate(),
    text: d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }),
    tz: timeZone,
  };
};
// Minimal .ics so "Add to Calendar" works on iPhone, Mac, Outlook and Google.
function icsHref(title, startIso, endIso) {
  const f = (iso) => new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const end = endIso || new Date(new Date(startIso).getTime() + 3600000).toISOString();
  const body = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Magellan International//Account//EN", "BEGIN:VEVENT",
    `UID:${f(startIso)}-${title.length}@magellan.international`, `DTSTAMP:${f(new Date().toISOString())}`, `DTSTART:${f(startIso)}`, `DTEND:${f(end)}`,
    `SUMMARY:${title.replace(/[,;\\]/g, " ")}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  return "data:text/calendar;charset=utf-8," + encodeURIComponent(body);
}

export default function Home({ profile }) {
  const [orders, setOrders] = useState({ loading: true, items: [] });
  const courses = useRemote("/customer/courses?limit=20");
  const files = useRemote("/customer/library?limit=3");
  useEffect(() => {
    const c = new AbortController();
    listCommerceOrders(c.signal).then((d) => {
      if (c.signal.aborted) return;
      const items = Array.isArray(d?.items) ? d.items.filter((o) => validOrder(o, o.id)) : [];
      setOrders({ items: items.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)) });
    }).catch(() => { if (!c.signal.aborted) setOrders({ items: [], error: true }); });
    return () => c.abort();
  }, []);
  const open = orders.items.filter(OPEN).slice(0, 3);
  const enrollments = courses.data?.items || [];
  const learning = enrollments.find((e) => !e.expiresAt || e.expiresAt > Date.now());
  const upcoming = enrollments
    .filter((e) => e.session?.startAt && new Date(e.session.startAt).getTime() > Date.now())
    .sort((a, b) => new Date(a.session.startAt) - new Date(b.session.startAt))[0];
  const next = upcoming && when(upcoming.session.startAt, upcoming.session.timeZone);
  const first = (profile?.name || "").split(" ")[0];
  return (
    <div className="ca-home">
      {first && <p className="ios-subhead ca-hello">Hi {first}</p>}
      {open.length > 0 && (
        <section aria-labelledby="ca-home-orders">
          <h2 id="ca-home-orders" className="ios-group-header"><span>Your orders</span><Link className="ca-see-all" to="/customer/access/orders">See all</Link></h2>
          <div className="ca-home-orders">
            {open.map((o) => {
              const [word, tone] = buyerStatus(o);
              return (
                <Link key={o.id} to={`/customer/access/orders/${o.id}`} className="ios-card ios-sheen ca-order-card ios-press">
                  <span className="ca-order-top">
                    <span className="ios-tile" data-tint={o.kind === "course_enrollment" ? "purple" : "gold"}>{o.kind === "course_enrollment" ? <GraduationCap aria-hidden="true" /> : <Package aria-hidden="true" />}</span>
                    <span className="ios-row-main"><span className="ios-row-title">{orderTitle(o)}</span><span className="ios-row-sub">{shortRef(o)}</span></span>
                    <span className="ios-capsule" data-tone={tone}>{word}</span>
                  </span>
                  <Tracker order={o} compact />
                </Link>
              );
            })}
          </div>
        </section>
      )}
      <div className="ca-home-grid">
        {learning && (
          <Link to={`/customer/access/courses/${learning.id}`} className="ios-card ios-sheen ca-home-tile ios-press">
            <span className="ios-caption">Continue learning</span>
            <span className="ios-headline">{learning.title}</span>
            {learning.expiresAt && <span className="ios-footnote">Access until {new Date(learning.expiresAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span>}
            <span className="ios-btn ca-home-cta">Continue</span>
          </Link>
        )}
        {next && (
          <div className="ios-card ios-sheen ca-home-tile">
            <span className="ios-caption">Next live session</span>
            <span className="ca-session">
              <span className="ca-date-chip"><small>{next.wd}</small>{next.num}</span>
              <span><span className="ios-headline">{upcoming.title}</span><span className="ios-footnote">{next.text} your time</span></span>
            </span>
            <a className="ios-btn ios-btn-tinted ca-home-cta" href={icsHref(upcoming.title, upcoming.session.startAt, upcoming.session.endAt)} download="magellan-session.ics">
              <CalendarPlus size={16} aria-hidden="true" /> Add to Calendar
            </a>
          </div>
        )}
      </div>
      {!open.length && !learning && !next && !orders.loading && !courses.loading && (
        <p className="ca-notice">Nothing in progress. Your orders, courses and live sessions will show up here.</p>
      )}
      <section aria-labelledby="ca-home-files">
        <h2 id="ca-home-files" className="ios-group-header"><span>Free resources</span><Link className="ca-see-all" to="/customer/access/files">See all</Link></h2>
        {files.data?.items?.length ? (
          <ul className="ios-group">
            {files.data.items.map((r) => (
              <li key={r.id}>
                <Link className="ios-row" to="/customer/access/files">
                  <span className="ios-tile" data-tint="red"><FileText aria-hidden="true" /></span>
                  <span className="ios-row-main"><span className="ios-row-title">{r.title || "Resource"}</span>{r.category && <span className="ios-row-sub">{r.category}</span>}</span>
                  <ChevronRight size={16} className="ios-chevron" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        ) : <p className="ios-group-footer">{files.loading ? "Loading…" : "Free guides and checklists appear here."}</p>}
      </section>
      <p className="ios-group-footer ca-refund">{REFUND_RULE}</p>
    </div>
  );
}
